/**
 * Customer "My Invoices" tab — The Indian Pest Control branded.
 * Shows GST / Non-GST badges, stats, view / download / print PDF.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Receipt, Download, Printer, Search, Loader2, X, Calendar, IndianRupee,
  CheckCircle2, Clock, AlertCircle, XCircle, FileText, Eye, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customers/me/invoices`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  partial: { label: 'Partial', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: IndianRupee },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  overdue: { label: 'Overdue', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: XCircle },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`} data-testid={`inv-status-${status}`}>
      <Icon size={10} /> {meta.label}
    </span>
  );
}

function GstBadge({ isGst }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isGst ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`} data-testid={`inv-gst-${isGst ? 'gst' : 'nongst'}`}>
      {isGst ? 'GST' : 'Non-GST'}
    </span>
  );
}

export default function CustomerInvoicesTab() {
  const { token } = useCustomerAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_billed: 0, paid: 0, outstanding: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(API, { headers: { Authorization: `Bearer ${token}` } });
      const data = r.data || {};
      setRows(data.items || []);
      setStats(data.stats || { total: 0, total_billed: 0, paid: 0, outstanding: 0 });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering
  const filtered = rows.filter(r => {
    if (search) {
      const s = search.toLowerCase();
      const match = (r.invoice_number || '').toLowerCase().includes(s) ||
        (r.service_name || '').toLowerCase().includes(s) ||
        (r.customer_name || '').toLowerCase().includes(s);
      if (!match) return false;
    }
    if (status && r.payment_status !== status) return false;
    if (dateFrom && (r.invoice_date || '') < dateFrom) return false;
    if (dateTo && (r.invoice_date || '') > dateTo) return false;
    return true;
  });

  const downloadPDF = async (inv) => {
    try {
      const r = await axios.get(`${API}/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TheIndianPestControl_Invoice_${inv.invoice_number.replace(/.*-/, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e)); }
  };

  const printPDF = async (inv) => {
    try {
      const r = await axios.get(`${API}/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => { try { w.print(); } catch { /* ignored */ } });
      }
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="customer-invoices-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2"><Receipt size={18} className="text-emerald-700" /> My Invoices</h2>
          <p className="text-xs sm:text-sm text-slate-500">All invoices from <b>The Indian Pest Control</b>. Download, print, or view payment status.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="h-8 text-xs" data-testid="inv-refresh"><RefreshCw size={12} className="mr-1" /> Refresh</Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Invoices" value={stats.total} Icon={FileText} tone="slate" />
        <StatCard label="Total Billed" value={fmtMoney(stats.total_billed)} Icon={IndianRupee} tone="emerald" />
        <StatCard label="Paid" value={fmtMoney(stats.paid)} Icon={CheckCircle2} tone="blue" />
        <StatCard label="Outstanding" value={fmtMoney(stats.outstanding)} Icon={AlertCircle} tone="rose" />
      </div>

      {/* Payment status notifications */}
      {rows.some(r => r.payment_status === 'overdue') && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="overdue-notice">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Payment Overdue</span> — You have {rows.filter(r => r.payment_status === 'overdue').length} overdue invoice(s) totalling {fmtMoney(rows.filter(r => r.payment_status === 'overdue').reduce((s, r) => s + (r.amount_due || 0), 0))}. Please clear them at the earliest.
          </div>
        </div>
      )}
      {rows.some(r => r.payment_status === 'pending') && !rows.some(r => r.payment_status === 'overdue') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="pending-notice">
          <Clock size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Payment Pending</span> — You have {rows.filter(r => r.payment_status === 'pending').length} pending invoice(s). Payment is due soon.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice # or service…" className="pl-9 h-10" data-testid="inv-search" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-status-filter">
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" data-testid="inv-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" data-testid="inv-date-to" />
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Invoice #</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Service</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Due</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-right px-4 py-2.5">Balance</th>
              <th className="text-center px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={9} className="py-10 text-center text-slate-400"><Loader2 className="inline animate-spin" size={18} /> Loading…</td></tr>)}
            {!loading && filtered.length === 0 && (<tr><td colSpan={9} className="py-12 text-center text-slate-400">No invoices yet.</td></tr>)}
            {!loading && filtered.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-semibold text-slate-900">{r.invoice_number}</td>
                <td className="px-4 py-2.5"><GstBadge isGst={r.is_gst} /></td>
                <td className="px-4 py-2.5 text-slate-700">{r.service_name || '—'}</td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.invoice_date)}</td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.due_date)}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{fmtMoney(r.total_amount)}</td>
                <td className="px-4 py-2.5 text-right text-rose-700 font-semibold">{fmtMoney(r.amount_due)}</td>
                <td className="px-4 py-2.5 text-center"><StatusBadge status={r.payment_status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setActive(r)} data-testid={`inv-view-${r.id}`} title="View"><Eye size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPDF(r)} data-testid={`inv-download-${r.id}`} title="Download"><Download size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => printPDF(r)} data-testid={`inv-print-${r.id}`} title="Print"><Printer size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5">
        {loading && <div className="text-center text-slate-400 py-8"><Loader2 className="inline animate-spin" size={18} /></div>}
        {!loading && filtered.length === 0 && <div className="text-center text-slate-400 py-8 bg-white border border-slate-200 rounded-2xl">No invoices yet.</div>}
        {!loading && filtered.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3.5" data-testid={`inv-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm truncate">{r.invoice_number}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <GstBadge isGst={r.is_gst} />
                  <span className="text-[11px] text-slate-500 truncate">{r.service_name || '—'}</span>
                </div>
              </div>
              <StatusBadge status={r.payment_status} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Date: </span><span className="text-slate-700">{fmtDate(r.invoice_date)}</span></div>
              <div><span className="text-slate-500">Due: </span><span className="text-slate-700">{fmtDate(r.due_date)}</span></div>
              <div><span className="text-slate-500">Total: </span><span className="font-semibold text-slate-900">{fmtMoney(r.total_amount)}</span></div>
              <div><span className="text-slate-500">Balance: </span><span className="font-semibold text-rose-700">{fmtMoney(r.amount_due)}</span></div>
            </div>
            <div className="flex gap-1.5 mt-3">
              <Button variant="outline" size="sm" onClick={() => setActive(r)} className="flex-1" data-testid={`inv-view-m-${r.id}`}><Eye size={13} className="mr-1" /> View</Button>
              <Button variant="outline" size="sm" onClick={() => downloadPDF(r)} className="flex-1" data-testid={`inv-dl-m-${r.id}`}><Download size={13} className="mr-1" /> PDF</Button>
              <Button variant="outline" size="sm" onClick={() => printPDF(r)} data-testid={`inv-pr-m-${r.id}`}><Printer size={13} /></Button>
            </div>
          </div>
        ))}
      </div>

      {active && <InvoiceDetailModal invoice={active} onClose={() => setActive(null)} onDownload={() => downloadPDF(active)} onPrint={() => printPDF(active)} />}
    </div>
  );
}

function StatCard({ label, value, Icon, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</span>
        <Icon size={14} className="opacity-60" />
      </div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onDownload, onPrint }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="inv-detail-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white">
          <div>
            <div className="text-xs opacity-90">The Indian Pest Control</div>
            <div className="font-bold text-sm flex items-center gap-2">
              Invoice {invoice.invoice_number}
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${invoice.is_gst ? 'bg-white/30' : 'bg-amber-400/50'}`}>
                {invoice.is_gst ? 'GST' : 'Non-GST'}
              </span>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="inv-modal-close" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <StatusBadge status={invoice.payment_status} />
            <div className="text-xs text-slate-500">Issued {fmtDate(invoice.invoice_date)} · Due {fmtDate(invoice.due_date)}</div>
          </div>

          {/* Payment status change notification */}
          {invoice.payment_status === 'pending' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="inv-detail-pending-notice">
              <Clock size={14} className="flex-shrink-0" />
              <span>Payment is pending. Please complete payment before the due date.</span>
            </div>
          )}
          {invoice.payment_status === 'overdue' && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800 flex items-center gap-2" data-testid="inv-detail-overdue-notice">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>This invoice is overdue. Please clear the balance immediately.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Customer</div>
              <div className="text-sm font-semibold text-slate-900">{invoice.customer_name}</div>
              <div className="text-xs text-slate-600">{invoice.customer_phone}</div>
              {invoice.customer_email && <div className="text-xs text-slate-600 truncate">{invoice.customer_email}</div>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Service</div>
              <div className="text-sm font-semibold text-slate-900">{invoice.service_name || '—'}</div>
              {invoice.service_date && <div className="text-xs text-slate-600">Performed: {fmtDate(invoice.service_date)}</div>}
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-emerald-700 text-white text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Amount</th></tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{it.description}</td>
                    <td className="px-3 py-2 text-right">{it.quantity}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(it.rate)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ml-auto max-w-xs text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmtMoney(invoice.subtotal)}</span></div>
            {(invoice.discount || 0) > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount</span><span>– {fmtMoney(invoice.discount)}</span></div>}
            {invoice.is_gst && <div className="flex justify-between"><span className="text-slate-500">GST ({invoice.gst_rate}%)</span><span>{fmtMoney(invoice.gst_amount)}</span></div>}
            <div className="flex justify-between border-t border-slate-200 pt-1 font-bold text-base"><span>Total</span><span>{fmtMoney(invoice.total_amount)}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{fmtMoney(invoice.amount_paid)}</span></div>
            <div className="flex justify-between font-bold text-rose-700"><span>Balance Due</span><span>{fmtMoney(invoice.amount_due)}</span></div>
          </div>

          {(invoice.payment_mode || invoice.payment_transaction_id) && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-xs">
              <div className="font-semibold text-emerald-900 mb-1 text-sm">Payment</div>
              {invoice.payment_mode && <div><span className="text-slate-500">Mode:</span> <span className="font-semibold">{invoice.payment_mode}</span></div>}
              {invoice.payment_transaction_id && <div className="truncate"><span className="text-slate-500">Txn ID:</span> <span className="font-mono">{invoice.payment_transaction_id}</span></div>}
              {invoice.payment_date && <div><span className="text-slate-500">Paid on:</span> {fmtDate(invoice.payment_date)}</div>}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex gap-2 bg-slate-50">
          <Button onClick={onDownload} className="flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="inv-modal-download"><Download size={14} className="mr-1.5" /> Download PDF</Button>
          <Button variant="outline" onClick={onPrint} className="flex-1" data-testid="inv-modal-print"><Printer size={14} className="mr-1.5" /> Print</Button>
        </div>
      </div>
    </div>
  );
}
